import { register, trackEvent } from '@opendoor/shared-fe/build/analytics';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Zones_buyer_getZonesByTextQuery_zones as Zone } from 'src/api/queries/__generated__/Zones';

import {
  OdProtosCasaData_Result,
  OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus,
} from '../__generated__/globalTypes';
import {
  GetSellerInputQuery_reception_getSellerInput_sellerInput_homeValue_comps,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals,
} from '../api/queries/__generated__/GetSellerInputQuery';
import {
  generateDenialReasonDisplayString,
  getLeadState,
  getOpendoorDirectDenialReason,
  getProductOfferingsMap,
} from '../api/sellerInputToLead';
import { DenialReason, PartnershipReferral, ProductOffering } from '../declarations';
import { getMarketDisplayName } from '../helpers/markets';
import { api } from './api';

export type SellerInputAddress = {
  city: string;
  isMissingUnitNumber: boolean;
  isInvalidUnitNumber: boolean;
  lat: number;
  lng: number;
  postalCode: string;
  state: string;
  street: string;
  street1?: string;
  uuid: string;
  unitNumber?: string;
};

export type AnswerPrefill = {
  value: string | number;
  source?: string;
};

export type SavedAnswer =
  | boolean
  | string
  | number
  | { _none_: boolean }
  | { skipped?: boolean; other?: boolean; zones?: Array<Zone> }
  | { min_price?: number; max_price?: number }
  | { min?: string | number; max?: string | number }
  | Record<string, boolean>;

export type Listing = {
  is_listed_and_eligible?: boolean;
  list_date?: string;
  listing_id?: string;
  listing_photos?: Array<string>;
  realtor_name?: string;
  realtor_email?: string;
};

export type SellerInput = {
  address: SellerInputAddress;
  answerPrefills: Record<string, AnswerPrefill>;
  channel: string;
  customerUUID?: string;
  denialCode?: string;
  denialReason?: string;
  sellerFlowName: string;
  experimentEntityId: string;
  hasHomebuilderSalesAssociate: boolean;
  homeValuation?: number;
  homeValuationComps?: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_homeValue_comps>;
  homeEstimateMin?: number;
  homeEstimateMax?: number;
  homeEstimateVariance?: number;

  isAddressAutoVerified: boolean;
  isPollingCustomerUUID: boolean;
  isModifyingAddress: boolean;
  isVerifyingAddress: boolean;
  lastCompletedSlug?: string;
  lastListing: Listing;
  lastListingPhotos: Array<string>;
  leadState: string;
  leadToken: string;
  market: string;
  marketDisplayName: string;
  nearFloodZone: boolean;
  odDirectDenialReasons: Array<DenialReason | undefined>;
  partnerships: Array<
    | GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals
    | PartnershipReferral
  >;
  productOfferings: Array<ProductOffering>;
  savedAnswers: Record<string, SavedAnswer>;
  changedAnswers: Record<string, SavedAnswer | null>;
  sellerFlowUUID: string;
  denialPartnerIdentifier: string;
};

const initialSellerInputState: SellerInput = {
  answerPrefills: {},
  lastListing: {},
  lastListingPhotos: [],
  odDirectDenialReasons: [],
  partnerships: [],
  productOfferings: [],
  savedAnswers: {},
  changedAnswers: {},

  //These seem like reasonable defaults
  //but may need to move them to be undefined
  hasHomebuilderSalesAssociate: false,
  isAddressAutoVerified: true,
  isPollingCustomerUUID: false,
  isModifyingAddress: false,
  isVerifyingAddress: false,
  nearFloodZone: false,

  //None of these have good defaults
  //but by having a default value we simplify
  //working with this state object
  channel: '',
  customerUUID: undefined,
  experimentEntityId: '',
  leadState: '',
  leadToken: '',
  market: '',
  marketDisplayName: '',
  sellerFlowUUID: '',
  sellerFlowName: '',
  address: {
    city: '',
    isMissingUnitNumber: false,
    isInvalidUnitNumber: false,
    lat: 0,
    lng: 0,
    postalCode: '',
    state: '',
    street: '',
    uuid: '',
  },
  denialPartnerIdentifier: '',
};

export const slice = createSlice({
  name: 'sellerInput',
  initialState: initialSellerInputState,
  reducers: {
    updateSellerInputHumanDetails: (
      state,
      action: PayloadAction<{ uuid?: string; email?: string; name?: string; phoneNumber?: string }>,
    ) => {
      if (action.payload.uuid) {
        state.customerUUID = action.payload.uuid;
      }
      if (action.payload.email) {
        state.answerPrefills['seller.email'] = { value: action.payload.email };
      }
      if (action.payload.name) {
        state.answerPrefills['seller.full_name'] = { value: action.payload.name };
      }
      if (action.payload.phoneNumber) {
        state.answerPrefills['seller.phone_number'] = {
          value: action.payload.phoneNumber,
        };
      }
    },

    changeAnswers: (
      state,
      action: PayloadAction<Array<{ key: string; answer: SavedAnswer | null }>>,
    ) => {
      action.payload.forEach(({ key, answer }) => {
        // currently the buying modal needs to be able to nest keys in order to work
        // to support that we allow keys to be of the form: root##node##leaf
        // which then will get expanded to changedAnswers: {root: {node: {leaf: value}}}
        // Once we identify if/where downstream these values are used we should adjust
        // the buying modal to work on keys set directly to changedAnswers
        if (key.indexOf('##') !== -1) {
          const keys = key.split('##');
          let node = state.changedAnswers[keys[0]] ?? {};
          const root = node;
          keys.forEach((k, idx) => {
            if (idx !== 0) {
              if (idx == keys.length - 1) {
                node[k] = answer;
              } else {
                node[k] = {};
              }
              node = node[k];
            }
          });
          state.changedAnswers[keys[0]] = root;
        } else {
          state.changedAnswers[key] = answer;
        }
      });
    },

    updateSellerInputAfterUpdateAnswers: (
      state,
      action: PayloadAction<{
        isAddressAutoVerified: boolean;
        channel: string;
        customerUuid: string;
        denialCode?: string;
        denialReason?: string;
        lastCompletedSlug?: string;
        partnerships?: Array<PartnershipReferral>;
        productOfferings: Array<ProductOffering>;
        serverAnswers: Record<string, unknown>;
        leadState: string;
        submittedAnswers: Record<string, unknown>;
      }>,
    ) => {
      //answersErrors: state
      //  .get('answersErrors')
      //  .filterNot((_v, k) => has(action.payload.submittedAnswers, k)),
      //state.answerErrors = state.answerErrors.filter((key) => {
      //  !action.payload.submittedAnswers[key];
      //});
      Object.keys(action.payload.submittedAnswers).forEach((key: string) => {
        if (typeof state.changedAnswers[key] !== 'undefined') {
          //TODO check if this is right for immer
          delete state.changedAnswers[key];
        }
      });

      state.channel = action.payload.channel;
      state.denialCode = action.payload.denialCode;
      state.customerUUID = action.payload.customerUuid;
      state.denialReason = action.payload.denialReason;
      state.isAddressAutoVerified = action.payload.isAddressAutoVerified;
      state.lastCompletedSlug = action.payload.lastCompletedSlug;
      state.leadState = action.payload.leadState;
      state.partnerships = action.payload.partnerships ?? [];
      state.productOfferings = action.payload.productOfferings || [];
      state.savedAnswers = action.payload.serverAnswers as Record<string, SavedAnswer>;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(api.endpoints.getCustomerUuid.matchFulfilled, (state, { payload }) => {
      if (payload) {
        state.customerUUID = payload;
      }
    });

    builder.addMatcher(api.endpoints.modifyAddress.matchFulfilled, (state, { payload }) => {
      state.isModifyingAddress = false;
      const sellerInput = payload.reception.modifySellerAddress?.sellerInput;
      if (sellerInput) {
        const productOfferings = getProductOfferingsMap(sellerInput);
        const opendoorDirectDenialReason = getOpendoorDirectDenialReason(sellerInput);
        if (sellerInput.address) {
          //TODO: setting this to a new object makes it so that we update unnecessarily
          state.address = {
            city: sellerInput.address.city,
            isMissingUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE,
            ),
            isInvalidUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE,
            ),
            lat: sellerInput.address.latitude,
            lng: sellerInput.address.longitude,
            postalCode: sellerInput.address.postalCode,
            state: sellerInput.address.state,
            street: `${sellerInput.address.street}${
              sellerInput.address.unitNumber ? ` #${sellerInput.address.unitNumber}` : ''
            }`,
            street1: sellerInput.address.street,
            uuid: sellerInput.address.uuid,
            unitNumber: sellerInput.address.unitNumber,
          };
        }
        state.answerPrefills = JSON.parse(sellerInput.answerPrefillsJson ?? '{}');
        state.savedAnswers = JSON.parse(sellerInput.answersJson ?? '{}');

        state.channel = sellerInput.channel;
        state.customerUUID = sellerInput.customerUuid;
        state.hasHomebuilderSalesAssociate = sellerInput.partnershipReferrals?.some(
          ({ type, userId }) => type === 'home_builder' && !!userId,
        );

        state.experimentEntityId = sellerInput.experimentEntityId;

        state.homeValuation = sellerInput.homeValue?.headlinePrice?.value;
        state.homeEstimateMin = sellerInput.homeValue?.headlineRangeLower?.value;
        state.homeEstimateMax = sellerInput.homeValue?.valuationCeilingCents;
        state.homeEstimateVariance = sellerInput.homeValue?.variance ?? undefined;
        state.homeValuationComps = sellerInput.homeValue?.comps;

        state.denialPartnerIdentifier = sellerInput.denialPartnerIdentifier;

        state.isAddressAutoVerified =
          sellerInput.addressVerifiedStatus ===
          OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.AUTO_VERIFIED;

        // TODO see if this ever changes
        state.isPollingCustomerUUID = false;

        state.lastCompletedSlug = sellerInput.sellerFlowLastCompletedSlug;
        state.leadToken = sellerInput.uuid;
        state.leadState = getLeadState(
          sellerInput,
          !!state.address.isMissingUnitNumber,
          !!state.address.isInvalidUnitNumber,
        );
        state.lastListing = {
          is_listed_and_eligible: sellerInput.isListed,
          list_date: sellerInput.homeDetails?.lastListing?.listDate,
          listing_id: sellerInput.homeDetails?.lastListing?.listingId,
          listing_photos: sellerInput.homeDetails?.lastListing?.listingPhotos,
          realtor_name: sellerInput.homeDetails?.lastListing?.realtorName,
          realtor_email: sellerInput.homeDetails?.lastListing?.realtorEmail,
        };
        state.lastListingPhotos = sellerInput.homeDetails?.photos ?? [];
        // state.listingPhotos - TODO find if this is not used or missing
        state.sellerFlowUUID = sellerInput.sellerFlowUuid;
        state.sellerFlowName = sellerInput.sellerFlowName;
        state.partnerships = sellerInput.partnershipReferrals;
        state.market = sellerInput.region;
        state.nearFloodZone = !!sellerInput.homeDetails?.nearFloodZone;

        state.marketDisplayName = getMarketDisplayName(sellerInput.region);

        //since offerings are an object we need to deep copy to prevent unnecessary updates
        //this case comes up when we do not have a customer_uuid and we need to navigate to the hub
        //we try to load the lead several times before giving up
        //
        //If we create a new object reference here (or in offering.denialReasons)
        //then we will cause a pointless update which will also start over the customer_uuid polling
        const updatedOfferings: Array<string> = [];
        state.productOfferings.forEach((offering) => {
          const updatedOffering = productOfferings[offering.product];
          if (updatedOffering) {
            updatedOfferings.push(updatedOffering['product']);
            offering['product'] = updatedOffering['product'];
            offering['matched'] = updatedOffering['matched'];
            offering['denied'] = updatedOffering['denied'];
            offering['denialReason'] = updatedOffering['denialReason'];
            offering['qualified'] = updatedOffering['qualified'];

            // only update reasons if they are different in some way
            const same = offering['denialReasons'].every(
              (reason, idx) => reason == updatedOffering['denialReasons'][idx],
            );
            if (!same) {
              offering['denialReasons'] = updatedOffering['denialReasons'];
            }
          }
        });

        Object.keys(productOfferings).forEach((key) => {
          if (!updatedOfferings.includes(key)) {
            state.productOfferings.push(productOfferings[key]);
          }
        });

        //state.productOfferings = state.productOfferings;
        //
        state.odDirectDenialReasons = productOfferings['opendoor_direct']?.denialReasons || [];

        state.denialCode = opendoorDirectDenialReason;
        state.denialReason = generateDenialReasonDisplayString({
          denialReason: opendoorDirectDenialReason,
          maxLotSqFt: sellerInput.opendoorDirectBuyboxThresholds?.maxLotSqFt,
          minYearBuilt: sellerInput.opendoorDirectBuyboxThresholds?.minYearBuilt,
          addressStreet: sellerInput?.address?.street || '',
        });
      }
    });

    builder.addMatcher(api.endpoints.verifyAddress.matchFulfilled, (state, { payload }) => {
      state.isVerifyingAddress = false;
      const sellerInput = payload.reception.verifySellerAddress?.sellerInput;
      if (sellerInput) {
        const productOfferings = getProductOfferingsMap(sellerInput);
        const opendoorDirectDenialReason = getOpendoorDirectDenialReason(sellerInput);
        if (sellerInput.address) {
          //TODO: setting this to a new object makes it so that we update unnecessarily
          state.address = {
            city: sellerInput.address.city,
            isMissingUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE,
            ),
            isInvalidUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE,
            ),
            lat: sellerInput.address.latitude,
            lng: sellerInput.address.longitude,
            postalCode: sellerInput.address.postalCode,
            state: sellerInput.address.state,
            street: `${sellerInput.address.street}${
              sellerInput.address.unitNumber ? ` #${sellerInput.address.unitNumber}` : ''
            }`,
            street1: sellerInput.address.street,
            uuid: sellerInput.address.uuid,
            unitNumber: sellerInput.address.unitNumber,
          };
        }
        state.answerPrefills = JSON.parse(sellerInput.answerPrefillsJson ?? '{}');
        state.savedAnswers = JSON.parse(sellerInput.answersJson ?? '{}');

        state.channel = sellerInput.channel;
        state.customerUUID = sellerInput.customerUuid;
        state.hasHomebuilderSalesAssociate = sellerInput.partnershipReferrals?.some(
          ({ type, userId }) => type === 'home_builder' && !!userId,
        );

        state.experimentEntityId = sellerInput.experimentEntityId;

        state.homeValuation = sellerInput.homeValue?.headlinePrice?.value;
        state.homeValuationComps = sellerInput.homeValue?.comps;
        state.denialPartnerIdentifier = sellerInput.denialPartnerIdentifier;

        state.isAddressAutoVerified =
          sellerInput.addressVerifiedStatus ===
          OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.AUTO_VERIFIED;

        // TODO see if this ever changes
        state.isPollingCustomerUUID = false;

        state.lastCompletedSlug = sellerInput.sellerFlowLastCompletedSlug;
        state.leadToken = sellerInput.uuid;
        state.leadState = getLeadState(
          sellerInput,
          !!state.address.isMissingUnitNumber,
          !!state.address.isInvalidUnitNumber,
        );
        state.lastListing = {
          is_listed_and_eligible: sellerInput.isListed,
          list_date: sellerInput.homeDetails?.lastListing?.listDate,
          listing_id: sellerInput.homeDetails?.lastListing?.listingId,
          listing_photos: sellerInput.homeDetails?.lastListing?.listingPhotos,
          realtor_name: sellerInput.homeDetails?.lastListing?.realtorName,
          realtor_email: sellerInput.homeDetails?.lastListing?.realtorEmail,
        };
        state.lastListingPhotos = sellerInput.homeDetails?.photos ?? [];
        // state.listingPhotos - TODO find if this is not used or missing
        state.sellerFlowUUID = sellerInput.sellerFlowUuid;
        state.sellerFlowName = sellerInput.sellerFlowName;
        state.partnerships = sellerInput.partnershipReferrals;
        state.market = sellerInput.region;
        state.nearFloodZone = !!sellerInput.homeDetails?.nearFloodZone;

        state.marketDisplayName = getMarketDisplayName(sellerInput.region);

        //since offerings are an object we need to deep copy to prevent unnecessary updates
        //this case comes up when we do not have a customer_uuid and we need to navigate to the hub
        //we try to load the lead several times before giving up
        //
        //If we create a new object reference here (or in offering.denialReasons)
        //then we will cause a pointless update which will also start over the customer_uuid polling
        const updatedOfferings: Array<string> = [];
        state.productOfferings.forEach((offering) => {
          const updatedOffering = productOfferings[offering.product];
          if (updatedOffering) {
            updatedOfferings.push(updatedOffering['product']);
            offering['product'] = updatedOffering['product'];
            offering['matched'] = updatedOffering['matched'];
            offering['denied'] = updatedOffering['denied'];
            offering['denialReason'] = updatedOffering['denialReason'];
            offering['qualified'] = updatedOffering['qualified'];

            // only update reasons if they are different in some way
            const same = offering['denialReasons'].every(
              (reason, idx) => reason == updatedOffering['denialReasons'][idx],
            );
            if (!same) {
              offering['denialReasons'] = updatedOffering['denialReasons'];
            }
          }
        });

        Object.keys(productOfferings).forEach((key) => {
          if (!updatedOfferings.includes(key)) {
            state.productOfferings.push(productOfferings[key]);
          }
        });

        //state.productOfferings = state.productOfferings;
        //
        state.odDirectDenialReasons = productOfferings['opendoor_direct']?.denialReasons || [];

        state.denialCode = opendoorDirectDenialReason;
        state.denialReason = generateDenialReasonDisplayString({
          denialReason: opendoorDirectDenialReason,
          maxLotSqFt: sellerInput.opendoorDirectBuyboxThresholds?.maxLotSqFt,
          minYearBuilt: sellerInput.opendoorDirectBuyboxThresholds?.minYearBuilt,
          addressStreet: sellerInput?.address?.street || '',
        });
      }
    });

    builder.addMatcher(api.endpoints.createSellerInput.matchFulfilled, (state, { payload }) => {
      state.isVerifyingAddress = false;
      const sellerInput =
        payload.reception.createSellerInputWithTrackingDataFromCookies?.sellerInput;
      if (sellerInput) {
        const productOfferings = getProductOfferingsMap(sellerInput);
        const opendoorDirectDenialReason = getOpendoorDirectDenialReason(sellerInput);
        if (sellerInput.address) {
          //TODO: setting this to a new object makes it so that we update unnecessarily
          state.address = {
            city: sellerInput.address.city,
            isMissingUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE,
            ),
            isInvalidUnitNumber: sellerInput.address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE,
            ),
            lat: sellerInput.address.latitude,
            lng: sellerInput.address.longitude,
            postalCode: sellerInput.address.postalCode,
            state: sellerInput.address.state,
            street: `${sellerInput.address.street}${
              sellerInput.address.unitNumber ? ` #${sellerInput.address.unitNumber}` : ''
            }`,
            street1: sellerInput.address.street,
            uuid: sellerInput.address.uuid,
            unitNumber: sellerInput.address.unitNumber,
          };
        }
        state.answerPrefills = JSON.parse(sellerInput.answerPrefillsJson ?? '{}');
        state.savedAnswers = JSON.parse(sellerInput.answersJson ?? '{}');

        state.channel = sellerInput.channel;
        state.customerUUID = sellerInput.customerUuid;
        state.hasHomebuilderSalesAssociate = sellerInput.partnershipReferrals?.some(
          ({ type, userId }) => type === 'home_builder' && !!userId,
        );

        state.experimentEntityId = sellerInput.experimentEntityId;

        state.homeValuation = sellerInput.homeValue?.headlinePrice?.value;
        state.homeValuationComps = sellerInput.homeValue?.comps;
        state.denialPartnerIdentifier = sellerInput.denialPartnerIdentifier;

        state.isAddressAutoVerified =
          sellerInput.addressVerifiedStatus ===
          OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.AUTO_VERIFIED;

        // TODO see if this ever changes
        state.isPollingCustomerUUID = false;

        state.lastCompletedSlug = sellerInput.sellerFlowLastCompletedSlug;
        state.leadToken = sellerInput.uuid;
        state.leadState = getLeadState(
          sellerInput,
          !!state.address.isMissingUnitNumber,
          !!state.address.isInvalidUnitNumber,
        );
        state.lastListing = {
          is_listed_and_eligible: sellerInput.isListed,
          list_date: sellerInput.homeDetails?.lastListing?.listDate,
          listing_id: sellerInput.homeDetails?.lastListing?.listingId,
          listing_photos: sellerInput.homeDetails?.lastListing?.listingPhotos,
          realtor_name: sellerInput.homeDetails?.lastListing?.realtorName,
          realtor_email: sellerInput.homeDetails?.lastListing?.realtorEmail,
        };
        state.lastListingPhotos = sellerInput.homeDetails?.photos ?? [];
        // state.listingPhotos - TODO find if this is not used or missing
        state.sellerFlowUUID = sellerInput.sellerFlowUuid;
        state.sellerFlowName = sellerInput.sellerFlowName;
        state.partnerships = sellerInput.partnershipReferrals;
        state.market = sellerInput.region;
        state.nearFloodZone = !!sellerInput.homeDetails?.nearFloodZone;

        state.marketDisplayName = getMarketDisplayName(sellerInput.region);

        //since offerings are an object we need to deep copy to prevent unnecessary updates
        //this case comes up when we do not have a customer_uuid and we need to navigate to the hub
        //we try to load the lead several times before giving up
        //
        //If we create a new object reference here (or in offering.denialReasons)
        //then we will cause a pointless update which will also start over the customer_uuid polling
        const updatedOfferings: Array<string> = [];
        state.productOfferings.forEach((offering) => {
          const updatedOffering = productOfferings[offering.product];
          if (updatedOffering) {
            updatedOfferings.push(updatedOffering['product']);
            offering['product'] = updatedOffering['product'];
            offering['matched'] = updatedOffering['matched'];
            offering['denied'] = updatedOffering['denied'];
            offering['denialReason'] = updatedOffering['denialReason'];
            offering['qualified'] = updatedOffering['qualified'];

            // only update reasons if they are different in some way
            const same = offering['denialReasons'].every(
              (reason, idx) => reason == updatedOffering['denialReasons'][idx],
            );
            if (!same) {
              offering['denialReasons'] = updatedOffering['denialReasons'];
            }
          }
        });

        Object.keys(productOfferings).forEach((key) => {
          if (!updatedOfferings.includes(key)) {
            state.productOfferings.push(productOfferings[key]);
          }
        });

        //state.productOfferings = state.productOfferings;
        //
        state.odDirectDenialReasons = productOfferings['opendoor_direct']?.denialReasons || [];

        state.denialCode = opendoorDirectDenialReason;
        state.denialReason = generateDenialReasonDisplayString({
          denialReason: opendoorDirectDenialReason,
          maxLotSqFt: sellerInput.opendoorDirectBuyboxThresholds?.maxLotSqFt,
          minYearBuilt: sellerInput.opendoorDirectBuyboxThresholds?.minYearBuilt,
          addressStreet: sellerInput?.address?.street || '',
        });
      }
    });

    builder.addMatcher(api.endpoints.getSellerInputs.matchFulfilled, (state, { payload }) => {
      if (payload.reception?.getSellerInput?.sellerInput) {
        const { address, ...inputs } = payload.reception.getSellerInput.sellerInput;

        const productOfferings = getProductOfferingsMap(
          payload.reception.getSellerInput.sellerInput,
        );
        const opendoorDirectDenialReason = getOpendoorDirectDenialReason(
          payload.reception.getSellerInput.sellerInput,
        );

        if (address) {
          //TODO: setting this to a new object makes it so that we update unnecessarily
          state.address = {
            city: address.city,
            isMissingUnitNumber: address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE,
            ),
            isInvalidUnitNumber: address.result?.includes(
              OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE,
            ),
            lat: address.latitude,
            lng: address.longitude,
            postalCode: address.postalCode,
            state: address.state,
            street: `${address.street}${address.unitNumber ? ` #${address.unitNumber}` : ''}`,
            street1: address.street,
            uuid: address.uuid,
            unitNumber: address.unitNumber,
          };
        }

        state.answerPrefills = JSON.parse(inputs.answerPrefillsJson ?? '{}');
        state.savedAnswers = JSON.parse(inputs.answersJson ?? '{}');

        state.channel = inputs.channel;
        state.customerUUID = inputs.customerUuid;
        state.hasHomebuilderSalesAssociate = inputs.partnershipReferrals?.some(
          ({ type, userId }) => type === 'home_builder' && !!userId,
        );

        state.experimentEntityId = inputs.experimentEntityId;

        state.homeValuation = inputs.homeValue?.headlinePrice?.value;
        state.homeValuationComps = inputs.homeValue?.comps;
        state.homeEstimateMin = inputs.homeValue?.headlineRangeLower?.value;
        state.homeEstimateMax = inputs.homeValue?.valuationCeilingCents;
        state.homeEstimateVariance = inputs.homeValue?.variance ?? undefined;

        state.denialPartnerIdentifier = inputs.denialPartnerIdentifier;
        state.isAddressAutoVerified =
          inputs.addressVerifiedStatus ===
          OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.AUTO_VERIFIED;

        // TODO see if this ever changes
        state.isPollingCustomerUUID = false;

        state.lastCompletedSlug = inputs.sellerFlowLastCompletedSlug;
        state.leadToken = inputs.uuid;
        state.leadState = getLeadState(
          payload.reception.getSellerInput.sellerInput,
          !!state.address.isMissingUnitNumber,
          !!state.address.isInvalidUnitNumber,
        );
        state.lastListing = {
          is_listed_and_eligible: inputs.isListed,
          list_date: inputs.homeDetails?.lastListing?.listDate,
          listing_id: inputs.homeDetails?.lastListing?.listingId,
          listing_photos: inputs.homeDetails?.lastListing?.listingPhotos,
          realtor_name: inputs.homeDetails?.lastListing?.realtorName,
          realtor_email: inputs.homeDetails?.lastListing?.realtorEmail,
        };
        state.lastListingPhotos = inputs.homeDetails?.photos ?? [];
        // state.listingPhotos - TODO find if this is not used or missing
        state.sellerFlowUUID = inputs.sellerFlowUuid;
        state.sellerFlowName = inputs.sellerFlowName;
        state.partnerships = inputs.partnershipReferrals;
        state.market = inputs.region;
        state.nearFloodZone = !!inputs.homeDetails?.nearFloodZone;

        state.marketDisplayName = getMarketDisplayName(inputs.region);

        //since offerings are an object we need to deep copy to prevent unnecessary updates
        //this case comes up when we do not have a customer_uuid and we need to navigate to the hub
        //we try to load the lead several times before giving up
        //
        //If we create a new object reference here (or in offering.denialReasons)
        //then we will cause a pointless update which will also start over the customer_uuid polling
        const updatedOfferings: Array<string> = [];
        state.productOfferings.forEach((offering) => {
          const updatedOffering = productOfferings[offering.product];
          if (updatedOffering) {
            updatedOfferings.push(updatedOffering['product']);
            offering['product'] = updatedOffering['product'];
            offering['matched'] = updatedOffering['matched'];
            offering['denied'] = updatedOffering['denied'];
            offering['denialReason'] = updatedOffering['denialReason'];
            offering['qualified'] = updatedOffering['qualified'];

            // only update reasons if they are different in some way
            const same = offering['denialReasons'].every(
              (reason, idx) => reason == updatedOffering['denialReasons'][idx],
            );
            if (!same) {
              offering['denialReasons'] = updatedOffering['denialReasons'];
            }
          }
        });

        Object.keys(productOfferings).forEach((key) => {
          if (!updatedOfferings.includes(key)) {
            state.productOfferings.push(productOfferings[key]);
          }
        });

        //state.productOfferings = state.productOfferings;
        //
        state.odDirectDenialReasons = productOfferings['opendoor_direct']?.denialReasons || [];

        state.denialCode = opendoorDirectDenialReason;
        state.denialReason = generateDenialReasonDisplayString({
          denialReason: opendoorDirectDenialReason,
          maxLotSqFt: inputs.opendoorDirectBuyboxThresholds?.maxLotSqFt,
          minYearBuilt: inputs.opendoorDirectBuyboxThresholds?.minYearBuilt,
          addressStreet: address?.street || '',
        });

        // TODO - I think these should be moved to FlowLoader, ideally we would
        // use useEffect and the react lifecycle to handle these kinds of
        // side-effects another alternative would be to setup listener
        // middlewares
        register({
          'Lead Token': state.leadToken,
          Market: state.market,
          'Reception BE': state.leadToken.length >= 32,
          'Seller Flow ID': state.sellerFlowUUID,
          'Seller Flow Name': 'default_flow', //FIXME
        });
        trackEvent('lead-state', 'change', 'ready', {
          address: state.address,
        });
      }
    });
  },
});

export const { updateSellerInputHumanDetails, changeAnswers, updateSellerInputAfterUpdateAnswers } =
  slice.actions;

export default slice.reducer;
